/**
 * Performance Monitoring Utilities
 * 
 * Provides performance tracking, metrics collection, and optimization helpers
 */

import { logger } from '@/services/logger';

type PerformanceMetric = {
  name: string;
  duration: number;
  timestamp: number;
  metadata?: Record<string, any>;
};

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private maxMetrics = 100; // Keep last 100 metrics
  private timers: Map<string, number[]> = new Map();

  /**
   * Start a performance timer
   */
  start(name: string): void {
    const startTimes = this.timers.get(name);
    if (startTimes) {
      startTimes.push(performance.now());
    } else {
      this.timers.set(name, [performance.now()]);
    }
  }

  /**
   * End a performance timer and record metric
   */
  end(name: string, metadata?: Record<string, any>): number {
    const startTimes = this.timers.get(name);
    if (!startTimes || startTimes.length === 0) {
      logger.warn(`[Performance] Timer "${name}" was not started`);
      return 0;
    }

    const startTime = startTimes.pop() as number;
    if (startTimes.length === 0) {
      this.timers.delete(name);
    }

    const duration = performance.now() - startTime;

    const metric: PerformanceMetric = {
      name,
      duration,
      timestamp: Date.now(),
      metadata,
    };

    this.metrics.push(metric);
    
    // Keep only last N metrics
    if (this.metrics.length > this.maxMetrics) {
      this.metrics.shift();
    }

    // Log slow operations
    if (duration > 1000) {
      logger.warn(`[Performance] Slow operation: ${name} took ${duration.toFixed(2)}ms`, metadata);
    } else if (__DEV__) {
      logger.debug(`[Performance] ${name}: ${duration.toFixed(2)}ms`);
    }

    return duration;
  }

  /**
   * Measure async operation
   */
  async measure<T>(
    name: string,
    operation: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    this.start(name);
    try {
      const result = await operation();
      this.end(name, { ...metadata, success: true });
      return result;
    } catch (error) {
      this.end(name, { ...metadata, success: false, error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  }

  /**
   * Measure sync operation
   */
  measureSync<T>(
    name: string,
    operation: () => T,
    metadata?: Record<string, any>
  ): T {
    this.start(name);
    try {
      const result = operation();
      this.end(name, { ...metadata, success: true });
      return result;
    } catch (error) {
      this.end(name, { ...metadata, success: false, error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  }

  /**
   * Get performance metrics
   */
  getMetrics(filter?: { name?: string; minDuration?: number }): PerformanceMetric[] {
    let filtered = [...this.metrics];

    if (filter?.name) {
      filtered = filtered.filter(m => m.name === filter.name);
    }

    const minDuration = filter?.minDuration;
    if (minDuration !== undefined) {
      filtered = filtered.filter(m => m.duration >= minDuration);
    }

    return filtered;
  }

  /**
   * Get average duration for an operation
   */
  getAverageDuration(name: string): number {
    const metrics = this.getMetrics({ name });
    if (metrics.length === 0) return 0;

    const total = metrics.reduce((sum, m) => sum + m.duration, 0);
    return total / metrics.length;
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics = [];
    this.timers.clear();
  }

  /**
   * Get summary statistics
   */
  getSummary(): {
    totalOperations: number;
    averageDuration: number;
    slowestOperations: Array<{ name: string; duration: number }>;
  } {
    if (this.metrics.length === 0) {
      return {
        totalOperations: 0,
        averageDuration: 0,
        slowestOperations: [],
      };
    }

    const totalDuration = this.metrics.reduce((sum, m) => sum + m.duration, 0);
    const averageDuration = totalDuration / this.metrics.length;

    const slowestOperations = [...this.metrics]
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 10)
      .map(m => ({ name: m.name, duration: m.duration }));

    return {
      totalOperations: this.metrics.length,
      averageDuration,
      slowestOperations,
    };
  }
}

// Singleton instance
export const performanceMonitor = new PerformanceMonitor();

/**
 * Performance decorator for functions
 */
export function measurePerformance(name?: string) {
  return function (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;
    const operationName = name || `${target.constructor.name}.${propertyKey}`;

    if (originalMethod.constructor.name === 'AsyncFunction') {
      descriptor.value = async function (...args: any[]) {
        return performanceMonitor.measure(operationName, () => originalMethod.apply(this, args));
      };
    } else {
      descriptor.value = function (...args: any[]) {
        return performanceMonitor.measureSync(operationName, () => originalMethod.apply(this, args));
      };
    }

    return descriptor;
  };
}

/**
 * React hook for measuring component render performance
 * Note: Import React in your component file to use this hook
 */
export function createUsePerformanceMeasure(React: any) {
  return function usePerformanceMeasure(componentName: string) {
    React.useEffect(() => {
      performanceMonitor.start(`${componentName}.render`);
      return () => {
        performanceMonitor.end(`${componentName}.render`);
      };
    });
  };
}

/**
 * Debounce function with performance tracking
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  operationName?: string
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      if (operationName) {
        performanceMonitor.measureSync(operationName, () => func(...args));
      } else {
        func(...args);
      }
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function with performance tracking
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number,
  operationName?: string
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      if (operationName) {
        performanceMonitor.measureSync(operationName, () => func(...args));
      } else {
        func(...args);
      }
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// Note: React hook is exported as a factory function
// Use: const usePerformanceMeasure = createUsePerformanceMeasure(React);

